import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from '@material-ui/core'
import useStyles from './styles'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import AccountRouterLink from 'components/account-router-link'
import PaymentAccountWarning from 'components/payment-account-warning'
import ExpirationDurationDropdown from './expiration-duration-dropdown'
import BookingTypePriceQuantity from './booking-type-price-quantity'
import BookingTypePrice from './booking-type-price'
import NoPaymentAccounts from './no-payment-accounts'
import ManageStripeAccountConfirmationDialog from 'pages/account/payments/payment-accounts/manage-stripe-account-confirmation-dialog'
import InfoAlert from 'components/info-alert'

const BookingTypePaymentOptions = ({
  errors,
  control,
  register,
  isLessonPacks,
  paymentAccounts,
  singleLessonPrice,
  watch,
  isEditFlow = false,
}) => {
  const classes = useStyles()
  const editClasses = useEditFormStyles()
  const fieldClass = isEditFlow ? editClasses.fieldInput : classes.formField
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )
  const selectedPaymentAccountId = watch?.('paymentAccountId')
  const selectedAccount = paymentAccounts.find(
    acc => acc.id === selectedPaymentAccountId,
  )
  const hasIncompleteStripeSetup =
    selectedAccount &&
    (!selectedAccount.detailsSubmitted || !selectedAccount.chargesEnabled)

  const paymentAccountHelper = !isLessonPacks
    ? 'Select a payment account to collect registration fees online for this booking type. If no payment account is selected, upfront payments will not be required.'
    : 'Select a payment account to collect online payments for this lesson pack. Upfront payments are required to offer a lesson pack.'

  return (
    <>
      {!isLessonPacks ? (
        <BookingTypePrice
          register={register}
          errors={errors}
          isEditFlow={isEditFlow}
        />
      ) : (
        <>
          <BookingTypePriceQuantity
            singleLessonPrice={singleLessonPrice}
            isEditFlow={isEditFlow}
          />
          <ExpirationDurationDropdown isEditFlow={isEditFlow} />
        </>
      )}
      <Box className={isEditFlow ? editClasses.fieldGroup : classes.formField}>
        {isEditFlow ? (
          <>
            <Typography className={editClasses.sectionHeading}>
              Payment Account{!isLessonPacks ? ' (Optional)' : ''}
            </Typography>
            <Typography className={editClasses.fieldHelper}>
              {paymentAccountHelper}
            </Typography>
          </>
        ) : (
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Payment Account {!isLessonPacks && '(Optional)'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {paymentAccountHelper}
            </Typography>
          </Box>
        )}
        {paymentAccounts.length > 0 || !isLessonPacks ? (
          <>
            <FormControl
              error={!!errors.paymentAccountId || hasIncompleteStripeSetup}
              variant="outlined"
              fullWidth
              className={fieldClass}
            >
              <InputLabel id="payment-account-label">
                Payment Account
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="payment-account-label"
                    label="Payment Account"
                    data-cy="paymentAccount"
                    value={value ?? ''}
                    onChange={onChange}
                  >
                    {paymentAccounts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} data-cy={opt.name}>
                        {opt.name}
                      </MenuItem>
                    ))}
                    <MenuItem key="none" value="">
                      <em>None</em>
                    </MenuItem>
                  </Select>
                )}
                name="paymentAccountId"
                control={control}
              />
            </FormControl>
            <Box mt={1}>
              <Button
                color="primary"
                size="small"
                to="/account/payments/payment-accounts"
                component={AccountRouterLink}
              >
                Manage payment accounts
              </Button>
            </Box>
            <PaymentAccountWarning
              paymentAccount={selectedAccount}
              hasUnresolvedPaymentAccount={
                !!selectedPaymentAccountId && !selectedAccount
              }
              onOpen={() => setIsManageStripeDialogOpen(true)}
            />
          </>
        ) : (
          <NoPaymentAccounts errors={errors.paymentAccountId} />
        )}
      </Box>
      <ManageStripeAccountConfirmationDialog
        paymentAccount={selectedAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => setIsManageStripeDialogOpen(false)}
      />

      {paymentAccounts.length > 0 ? (
        <Box
          className={isEditFlow ? editClasses.fieldGroup : classes.formField}
        >
          <InfoAlert mt={isEditFlow ? 0 : 2}>
            <Typography variant="body2">
              Please note that a 2.9% + $0.30 fee will be applied to the
              transaction total.
            </Typography>
          </InfoAlert>
        </Box>
      ) : null}
    </>
  )
}

export default BookingTypePaymentOptions
